import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function TimePeriodView({ apiBase, loading, setLoading, error, setError }) {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('day');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    // Set default dates
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
    setEndDate(today.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      loadData();
    }
  }, [period, startDate, endDate]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${apiBase}/analytics/by-time-period?period=${period}&start_date=${startDate}&end_date=${endDate}`
      );
      setData(response.data);
    } catch (err) {
      setError('Failed to load time period data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading data...</div>;
  }

  if (!data || data.length === 0) {
    return <div className="loading">No data available for the selected period</div>;
  }

  const labels = data.map(d => d.start_date);
  
  const totalCalls = data.reduce((sum, d) => sum + d.total_calls, 0);
  const totalCredits = data.reduce((sum, d) => sum + d.total_credits_used, 0);
  const totalCost = data.reduce((sum, d) => sum + d.total_cost, 0);
  const avgDaily = totalCalls / data.length;

  const timeChartData = {
    labels: labels,
    datasets: [{
      label: 'Calls',
      data: data.map(d => d.total_calls),
      borderColor: '#667eea',
      backgroundColor: 'rgba(102, 126, 234, 0.1)',
      tension: 0.4,
      fill: true
    }]
  };

  const creditsChartData = {
    labels: labels,
    datasets: [{
      label: 'Credits',
      data: data.map(d => d.total_credits_used),
      backgroundColor: '#48bb78'
    }]
  };

  const costChartData = {
    labels: labels,
    datasets: [{
      label: 'Cost ($)',
      data: data.map(d => d.total_cost / 100),
      backgroundColor: '#f56565'
    }]
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  };

  const costOptions = {
    ...barOptions,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return '$' + value.toFixed(2);
          }
        }
      }
    }
  };

  return (
    <>
      <div className="controls" style={{ marginTop: '20px' }}>
        <div className="control-group">
          <label htmlFor="period">Period</label>
          <select id="period" value={period} onChange={(e) => setPeriod(e.target.value)}>
            <option value="day">Daily</option>
            <option value="week">Weekly</option>
            <option value="month">Monthly</option>
          </select>
        </div>
        <div className="control-group">
          <label htmlFor="startDate">Start Date</label>
          <input 
            type="date" 
            id="startDate" 
            value={startDate} 
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="control-group">
          <label htmlFor="endDate">End Date</label>
          <input 
            type="date" 
            id="endDate" 
            value={endDate} 
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <button onClick={loadData}>🔄 Refresh</button>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Calls</h3>
          <div className="value">{totalCalls.toLocaleString()}</div>
          <div className="subvalue">{data.length} {period}(s)</div>
        </div>
        <div className="stat-card">
          <h3>Total Credits</h3>
          <div className="value">{totalCredits.toLocaleString()}</div>
          <div className="subvalue">ElevenLabs credits</div>
        </div>
        <div className="stat-card">
          <h3>Total Cost</h3>
          <div className="value cost">${(totalCost / 100).toFixed(2)}</div>
          <div className="subvalue">Your cost</div>
        </div>
        <div className="stat-card">
          <h3>Daily Average</h3>
          <div className="value">{avgDaily.toFixed(1)}</div>
          <div className="subvalue">calls per day</div>
        </div>
      </div>

      <div className="chart-grid">
        <div className="chart-container full-width">
          <h2>Calls Over Time</h2>
          <Line data={timeChartData} options={lineOptions} />
        </div>
        <div className="chart-container">
          <h2>Credits Usage</h2>
          <Bar data={creditsChartData} options={barOptions} />
        </div>
        <div className="chart-container">
          <h2>Cost Trend</h2>
          <Bar data={costChartData} options={costOptions} />
        </div>
      </div>

      <div className="table-container">
        <h2>Detailed Breakdown</h2>
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Calls</th>
              <th>Credits Used</th>
              <th>Cost</th>
              <th>Avg/Call</th>
            </tr>
          </thead>
          <tbody>
            {data.map((d, index) => (
              <tr key={index}>
                <td>{d.start_date}</td>
                <td>{d.total_calls}</td>
                <td>{d.total_credits_used.toLocaleString()}</td>
                <td className="cost">${(d.total_cost / 100).toFixed(2)}</td>
                <td>${(d.total_cost / d.total_calls / 100).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default TimePeriodView;

